import { captureMessage } from '@sentry/react';

import { getProxyAuthHeaders } from '@/auth/imsTokenStorage';
import { config } from '@/config/env';

export type Jsonable =
	| string
	| number
	| boolean
	| null
	| undefined
	| object
	| unknown;

// The Fetch standard rejects a keepalive request whose body passes 64 KiB, and the
// catch below makes that rejection silent. So check the size before asking for it.
const KEEPALIVE_BODY_LIMIT_BYTES = 64 * 1024;

// Browser memory, not delivery depth, sets these. The proxy owns the deep buffer.
const MAX_QUEUED_LINES = 100;
// A line count does not measure memory. One line can reach hundreds of kilobytes,
// so a count cap alone lets 100 lines hold far more than a tab can spare.
const MAX_QUEUED_BYTES = 2 * 1024 * 1024;
// A line above this is never queued. Rejecting one line beats evicting the many
// deliverable lines it would displace. It matches the proxy's own per-payload cap,
// so a line this queue refuses is one the proxy could not have buffered either.
const MAX_QUEUED_LINE_BYTES = 256 * 1024;
// After this many failed drains a line is destroyed rather than retried forever.
const MAX_SEND_ATTEMPTS = 3;
const DRAIN_DELAY_MS = 2_000;
// The proxy answers a browser send within its own short deadline, so a send that
// passes this one is not coming back. Set well above the proxy's answer time: the
// catch cannot tell "never arrived" from "arrived and slow", and treating a slow
// answer as a failure re-sends a line the proxy already took.
const SEND_TIMEOUT_MS = 15_000;
// A total proxy failure makes every line fail, so one report per line would flood
// Sentry. One report per kind per window is enough to see the event.
const REPORT_INTERVAL_MS = 60_000;
// The window alone would export nothing more after the first report of a burst, and
// a tab that closes takes the rest with it. So the window yields once this many
// destroyed lines have gone unreported.
const REPORT_VOLUME_THRESHOLD = MAX_QUEUED_LINES;

// The Fetch standard gives the whole page ONE 64 KiB budget for every in-flight
// keepalive request together. Sentry's own transport claims part of that same
// budget for the report below, and it stops asking for keepalive above 60 000
// bytes pending. So the unload flush takes less than the whole budget, and the
// queue past it is counted rather than sent.
//
// Keep this below KEEPALIVE_BODY_LIMIT_BYTES. That is what lets the unload flush
// test the running budget alone: a line this budget still admits is always small
// enough to ask for keepalive at all.
const KEEPALIVE_UNLOAD_BUDGET_BYTES = 48 * 1024;

// Sentry groups issues by the message string, so these must stay stable: no
// interpolated status, count or id. Line content never reaches Sentry, because a
// debug frame dump carries a live IMS access token.
const PROXY_DROP_MESSAGE = 'Splunk proxy dropped a browser log line';
const PROXY_REFUSED_MESSAGE = 'Splunk proxy refused a browser log line';
const QUEUE_DROP_MESSAGE = 'Browser log queue dropped a log line';
const SERIALIZE_FAIL_MESSAGE = 'Browser log line could not be serialized';

// The browser keeps a line only when the answer means the proxy never took it. A
// 5xx is the proxy itself failing. These two come from an intermediary instead: the
// Adobe ingress sits in front of ui-proxy and can refuse or time out a request
// before the Express route runs. The route itself answers neither status.
const RETRYABLE_PROXY_STATUSES = new Set([408, 429]);

const isRetryableProxyStatus = (status: number): boolean =>
	status >= 500 || RETRYABLE_PROXY_STATUSES.has(status);

// A 200 and the 202 that means the proxy buffered the line both land here. Every
// other non-retryable answer means the line is gone and the proxy never took it.
const isSuccessStatus = (status: number): boolean =>
	status >= 200 && status < 300;

let currentUserId = '';

/**
 * Records the signed-in user for every later log line. The UI resolves
 * `profile.authId` at sign-in, and every other service keys on that same value, so
 * stamping it here is what makes a browser log line findable by user.
 */
export const setLogUserId = (userId: string): void => {
	currentUserId = userId;
};

const byteLength = (value: string): number =>
	new TextEncoder().encode(value).byteLength;

interface SendResult {
	status: number;
	// True when the proxy's answer says the proxy destroyed the line. The proxy has
	// already counted that one, so this side reports it and counts nothing.
	proxyDropped: boolean;
}

interface QueuedLine {
	// The serialized body, not the built headers. A re-send must ask for the auth
	// headers again, because the IMS token rotates and a captured header goes stale.
	body: string;
	keepalive: boolean;
	attempts: number;
	// Held on the entry so every path that removes or restores one can correct the
	// running total without measuring the string again.
	bytes: number;
}

class Logger {
	private queue: QueuedLine[] = [];
	private queuedBytes = 0;
	private drainTimer: ReturnType<typeof setTimeout> | null = null;
	private draining = false;
	// The line a drain took out of the queue and has not settled. A page hide that
	// read the queue alone would miss it, and it is the line most likely to be
	// lost, because its request is the one the page interrupts.
	private inFlight: QueuedLine | null = null;
	// Set once flushOnUnload() has taken its count. Past that point a send that
	// answers late must not re-queue or count anything, because its line is
	// already counted.
	private stopped = false;
	private dropped = 0;
	private lastReportAt = new Map<string, number>();
	private lastReportedDropped = 0;

	/**
	 * Counts the log lines this browser destroyed: a queue eviction, a line too
	 * large to queue, a line whose metadata would not serialize, a line that used up
	 * its attempts, a line an intermediary refused before the proxy saw it, a queued
	 * line the page-hide flush could not carry, and a line a send still held when
	 * the page went away. It never counts a line the proxy itself rejected. The
	 * proxy has already counted those, and counting them on both sides would report
	 * several times the real loss.
	 */
	get droppedCount(): number {
		return this.dropped;
	}

	private report(
		kind: string,
		message: string,
		extra: Record<string, unknown>,
		// True when this report is about a line THIS browser destroyed. A proxy
		// drop is not one. It must not spend the volume budget below, or a burst
		// of queue evictions stays hidden behind one proxy report per window.
		ownsDrop = true,
	) {
		const now = Date.now();
		const last = this.lastReportAt.get(kind) ?? 0;
		const unreported = ownsDrop ? this.dropped - this.lastReportedDropped : 0;
		if (
			now - last < REPORT_INTERVAL_MS &&
			unreported < REPORT_VOLUME_THRESHOLD
		) {
			return;
		}
		this.lastReportAt.set(kind, now);
		// Each browser-drop report carries the cumulative droppedTotal, so this
		// marks the whole count exported.
		if (ownsDrop) this.lastReportedDropped = this.dropped;
		try {
			captureMessage(message, { level: 'warning', extra });
		} catch {
			// Reporting is best-effort. Logging must work even when Sentry does not.
		}
	}

	private async send(body: string, keepalive: boolean): Promise<SendResult> {
		// A stalled proxy connection can leave fetch() pending for the life of the
		// page. That is worse than a rejection: the line never reaches the queue,
		// and a stall inside drain() holds `draining` true, which stops every later
		// drain. The deadline turns that stall into the rejection the catch paths
		// already own. AbortSignal.timeout is not used here, because its timer
		// cannot be cleared and would outlive every settled send.
		//
		// The deadline must also cover the body read below, so the read happens
		// here rather than at the call site. fetch() resolves as soon as the headers
		// arrive, so a proxy that answers and then stalls its body leaves
		// response.json() pending for ever. A pending read is not a rejection, so no
		// catch can own it, and a deadline cleared at the end of the request would
		// leave exactly the stall this deadline exists to stop.
		const controller = new AbortController();
		const deadline = setTimeout(() => controller.abort(), SEND_TIMEOUT_MS);
		try {
			const response = await fetch(
				`${config.UI_PROXY_URL}/splunk-vite-proxy/proxy`,
				{
					method: 'POST',
					keepalive,
					headers: {
						'Content-Type': 'application/json',
						...getProxyAuthHeaders(),
					},
					body,
					signal: controller.signal,
				},
			);
			// A retryable answer keeps the line whatever the body says, so do not
			// spend the deadline reading one.
			if (isRetryableProxyStatus(response.status)) {
				return { status: response.status, proxyDropped: false };
			}
			return {
				status: response.status,
				proxyDropped: await this.readDroppedFlag(response),
			};
		} finally {
			clearTimeout(deadline);
		}
	}

	/**
	 * Reads whether the proxy's answer says the proxy destroyed the line.
	 *
	 * The body is parsed in its own try, and an unreadable body counts as
	 * delivered. A parse problem is not a transport failure, so it must never make
	 * the line queue again. The abort above lands here for the same reason.
	 */
	private async readDroppedFlag(response: Response): Promise<boolean> {
		try {
			const parsed = (await response.json()) as { dropped?: boolean } | null;
			return parsed?.dropped === true;
		} catch {
			return false;
		}
	}

	/**
	 * Keeps a line for a later attempt.
	 *
	 * This queue owns two failures only: the request never reached the proxy, and
	 * the proxy route itself failed. Every retryable Splunk failure belongs to the
	 * proxy's own buffer, and a line Splunk rejects for good belongs to no queue.
	 */
	private enqueue(line: QueuedLine) {
		// A call after flushOnUnload() has already taken its count has no drain
		// left to run it: the timer scheduled below never fires before the page
		// is torn down. Counting it here is the only way it is ever counted.
		if (this.stopped) {
			this.dropped += 1;
			this.report('page-hide', QUEUE_DROP_MESSAGE, {
				reason: 'page-hide',
				droppedTotal: this.dropped,
				queued: this.queue.length,
			});
			return;
		}
		if (line.bytes > MAX_QUEUED_LINE_BYTES) {
			this.dropped += 1;
			this.report('line-too-large', QUEUE_DROP_MESSAGE, {
				reason: 'line-too-large',
				droppedTotal: this.dropped,
				queued: this.queue.length,
			});
			return;
		}
		this.queue.push(line);
		this.queuedBytes += line.bytes;
		this.trimToCap();
		this.scheduleDrain();
	}

	// Runs after every operation that grows the queue, including the re-queue in
	// drain(). A cap applied only in enqueue() lets the queue pass its limit in the
	// window right after a re-queue, because a re-queued line never passes through
	// enqueue().
	private trimToCap() {
		// Keep the newest. During an outage the newest lines describe what is
		// happening now, and the oldest are the least useful.
		let evicted = 0;
		while (
			this.queue.length > 0 &&
			(this.queue.length > MAX_QUEUED_LINES ||
				this.queuedBytes > MAX_QUEUED_BYTES)
		) {
			const oldest = this.queue.shift();
			if (!oldest) break;
			this.queuedBytes -= oldest.bytes;
			evicted += 1;
		}
		if (evicted > 0) {
			this.dropped += evicted;
			this.report('queue-full', QUEUE_DROP_MESSAGE, {
				reason: 'queue-full',
				droppedTotal: this.dropped,
				queued: this.queue.length,
			});
		}
	}

	// Scheduled on demand, never a module-level interval: 61 modules import this
	// one, so a permanent timer would run in every test file that does not mock it.
	private scheduleDrain() {
		if (this.drainTimer || this.queue.length === 0) return;
		this.drainTimer = setTimeout(() => {
			this.drainTimer = null;
			void this.drain();
		}, DRAIN_DELAY_MS);
	}

	private async drain() {
		if (this.draining) return;
		this.draining = true;
		try {
			while (this.queue.length > 0) {
				const line = this.queue.shift();
				if (!line) break;
				this.queuedBytes -= line.bytes;
				line.attempts += 1;
				// Held where flushOnUnload() can see it. Between the shift above and
				// the answer below the line sits in no queue, so nothing else could
				// count it if the page went away now.
				this.inFlight = line;

				// A rejection means the request never reached the proxy, which reads
				// the same as the retryable answer tested below.
				const result = await this.send(line.body, line.keepalive).catch(
					() => null,
				);
				this.inFlight = null;

				// The page-hide flush has taken its count, and this line is inside it.
				// Counting it again, or putting it back into a queue the page is about
				// to destroy, would report one loss twice.
				if (this.stopped) return;

				if (result && !isRetryableProxyStatus(result.status)) {
					this.recordDiscardedAnswer(result);
					continue;
				}

				if (line.attempts >= MAX_SEND_ATTEMPTS) {
					this.dropped += 1;
					this.report('attempts-exhausted', QUEUE_DROP_MESSAGE, {
						reason: 'attempts-exhausted',
						droppedTotal: this.dropped,
						queued: this.queue.length,
					});
					// Stop the pass, for the same reason the re-queue below stops it.
					// Reaching this line means the send failed, so the proxy is down,
					// and an attempt spent on the next line now is an attempt lost.
					// Going on destroys the queue at twice the rate the attempt cap
					// sets, and the lines it costs are ones a recovery would deliver.
					break;
				}
				// Put it back at the front and stop this pass. The proxy is failing, so
				// working through the rest of the queue now would only lose attempts.
				this.queue.unshift(line);
				this.queuedBytes += line.bytes;
				// Lines that arrived while this send was in flight can have filled the
				// queue. A re-queue into a full queue can evict the line just pushed
				// back. That is correct: the alternative is unbounded growth.
				this.trimToCap();
				break;
			}
		} finally {
			this.draining = false;
			this.scheduleDrain();
		}
	}

	/**
	 * Records an answer that means the line is not coming back.
	 *
	 * Three shapes reach here. A `dropped` flag says the proxy destroyed the line,
	 * which is worth reporting but is already counted there. A success status means
	 * the proxy delivered the line or buffered it, so nothing was destroyed.
	 *
	 * The third shape is a refusal the proxy never counted, because the proxy never
	 * saw the line: the Adobe ingress in front of ui-proxy answers 403 for a
	 * credential it will not accept and 413 for a body it will not carry, both
	 * before the Express route runs. No other side can count that line, so this
	 * side must, or the loss is invisible on both sides.
	 */
	private recordDiscardedAnswer(result: SendResult) {
		if (result.proxyDropped) {
			this.report(
				'proxy-dropped',
				PROXY_DROP_MESSAGE,
				{
					reason: 'proxy-dropped',
					queued: this.queue.length,
					// Deliberately not counted in droppedCount: the proxy owns this one.
					browserDroppedTotal: this.dropped,
				},
				false,
			);
			return;
		}
		if (isSuccessStatus(result.status)) return;
		this.dropped += 1;
		this.report('proxy-refused', PROXY_REFUSED_MESSAGE, {
			reason: 'proxy-refused',
			// A number, never line content. It tells an engineer which refusal this
			// was without putting the status in the message Sentry groups on.
			status: result.status,
			droppedTotal: this.dropped,
			queued: this.queue.length,
		});
	}

	/**
	 * Delivers what the retry queue still holds when the page goes away.
	 *
	 * The queue is tab memory, so a tab close ends it, and that is the most common
	 * way it ends. A keepalive request outlives the page it started in. That is the
	 * same guarantee the first send of every small line already asks for, so these
	 * lines are sent rather than counted dead.
	 *
	 * Only what the budget cannot carry is counted. A line above the keepalive body
	 * limit is destroyed with the page, and so is a line past the page's shared
	 * byte budget. Nothing else can count those, so this side must.
	 *
	 * The line a drain is holding is counted here too. It sits in no queue, so this
	 * is the only place that can see it, and a request without keepalive dies with
	 * the page. The report names it apart from a buffered line, because a buffered
	 * line is a certain loss and this one may already have reached Splunk.
	 */
	flushOnUnload() {
		const pending = this.queue;
		this.queue = [];
		this.queuedBytes = 0;
		// Past this point the drain below must not re-queue or count its line.
		this.stopped = true;
		// A drain holds this one outside the queue, so no other side can count it.
		// Its request already carries the flag the line was queued with: a
		// keepalive request outlives the page and still arrives, and one without
		// keepalive dies here. A line without keepalive is above the keepalive body
		// limit, so it is above this budget too, and it can never be re-sent.
		const inFlightLost = this.inFlight && !this.inFlight.keepalive ? 1 : 0;
		this.inFlight = null;
		let budget = KEEPALIVE_UNLOAD_BUDGET_BYTES;
		let lost = inFlightLost;
		// Newest first. The newest lines describe what is happening now, and the
		// budget is smaller than a full queue, so the oldest are the ones to lose.
		for (let i = pending.length - 1; i >= 0; i -= 1) {
			const line = pending[i];
			if (line.bytes <= budget) {
				budget -= line.bytes;
				// Never awaited. The page ends before any answer arrives, and the
				// keepalive request carries the line on without this frame.
				void this.send(line.body, true).catch(() => undefined);
				continue;
			}
			lost += 1;
		}
		if (lost === 0) return;
		this.dropped += lost;
		this.report('page-hide', QUEUE_DROP_MESSAGE, {
			reason: 'page-hide',
			droppedTotal: this.dropped,
			queued: 0,
			// Named apart, the way bridge/logger.ts names its shutdown count. A
			// buffered line is a certain loss, because no request ever took it. A
			// line in a request the page interrupted may already sit in Splunk. Both
			// stay in the count, so a real loss is never hidden.
			buffered: lost - inFlightLost,
			inFlight: inFlightLost,
		});
	}

	private async log(level: string, message: string, meta: Jsonable = {}) {
		console.log(`[${level.toUpperCase()}]`, message, meta);

		// Frame dumps at debug level are most of this service's production Splunk
		// volume, and they carry a live IMS access token. Keep them in the console
		// and off the network in production.
		if (level === 'debug' && config.IS_PROD) {
			return;
		}

		// Callers pass arbitrary values as meta, and a cyclic object is ordinary in a
		// browser: a DOM node or a React event refers back to its parent. Such a line
		// can never be sent, so report it here and stop. Queueing it would fail the
		// same way on every drain.
		let body: string;
		try {
			body = JSON.stringify({
				level,
				message,
				meta,
				user_id: currentUserId,
			});
		} catch {
			console.error('Failed to serialize a log line for the Splunk proxy');
			// This browser destroyed the line, and it reached no proxy, so no other
			// side can count it. Its own kind keeps its own rate-limit window: a
			// serialization failure and an outage have different causes.
			this.dropped += 1;
			this.report('serialize-failed', SERIALIZE_FAIL_MESSAGE, {
				reason: 'serialize-failed',
				droppedTotal: this.dropped,
				queued: this.queue.length,
			});
			return;
		}
		const bytes = byteLength(body);
		const keepalive = bytes <= KEEPALIVE_BODY_LIMIT_BYTES;

		try {
			const result = await this.send(body, keepalive);
			// Keep the line only when the answer means the proxy never took it. Every
			// other answer, including the 202 that means the proxy buffered it, means
			// this browser is done with the line. Never re-send a permanent Splunk
			// rejection: the re-send can never deliver, and it makes the proxy count
			// the same destroyed line on every arrival.
			if (isRetryableProxyStatus(result.status)) {
				this.enqueue({ body, keepalive, attempts: 1, bytes });
				return;
			}
			this.recordDiscardedAnswer(result);
		} catch {
			// console.error, never logger.error: that would recurse through the send
			// that just failed.
			console.error('Failed to send log to Splunk proxy');
			this.enqueue({ body, keepalive, attempts: 1, bytes });
		}
	}

	error(message: string, meta: Jsonable = {}) {
		return this.log('error', message, meta);
	}

	warn(message: string, meta: Jsonable = {}) {
		return this.log('warn', message, meta);
	}

	info(message: string, meta: Jsonable = {}) {
		return this.log('info', message, meta);
	}

	debug(message: string, meta: Jsonable = {}) {
		return this.log('debug', message, meta);
	}
}

const singleton = new Logger();

export const logger = {
	error: (message: string, meta: Jsonable = {}) =>
		void singleton.error(message, meta),
	warn: (message: string, meta: Jsonable = {}) =>
		void singleton.warn(message, meta),
	info: (message: string, meta: Jsonable = {}) =>
		void singleton.info(message, meta),
	debug: (message: string, meta: Jsonable = {}) =>
		void singleton.debug(message, meta),
	droppedCount: (): number => singleton.droppedCount,
};

/**
 * Sends what the retry queue still holds when the page goes away.
 *
 * `main.tsx` calls this once, and it is deliberately not a listener registered on
 * import. 61 modules import this one, so an import-time listener would run in
 * every test file that does not mock it. The drain timer is on demand for that
 * same reason.
 *
 * `pagehide` is the one unload event mobile Safari fires reliably. A hide the
 * browser can restore keeps the queue: that page can come back and drain it in
 * the ordinary way, so counting those lines destroyed would report a false loss.
 */
export const registerLogFlushOnPageHide = (): (() => void) => {
	const onPageHide = (event: PageTransitionEvent) => {
		if (event.persisted) return;
		singleton.flushOnUnload();
	};
	window.addEventListener('pagehide', onPageHide);
	return () => window.removeEventListener('pagehide', onPageHide);
};
